import { createElement } from 'react'
import { RawType, SpanType } from '../../types'

const isInternal = (data: any) => {
  if (data.link_type === 'Web') {
    return false
  } else if (data.link_type === 'Document') {
    return true
  }
  return /^\/(?!\/)/.test(data.slug || data.url)
}

let getLink = (data: any) => {
  let url = data.slug || data.url
  if (data.tags && data.tags.includes('DEFAULT_FRONTPAGE')) {
    url = '/'
  }
  return url
}

const linkResolver = (data: any) => {
  return isInternal(data) ? { href: getLink(data) } : { href: data.url, target: '_blank' }
}

const getAttributes = (span: SpanType) => {
  const { data, type } = span
  if (type === 'hyperlink') {
    return { ...linkResolver(data) }
  } else if (type === 'em') {
    return { className: 'font-italic' }
  } else if (type === 'label') {
    if (data.label === 'button-primary' || data.label === 'button-secondary') {
      return { type: data.label.split('-')[1], to: data?.url, className: 'button-primary' }
    } else if (data.label === 'larger-paragraph') {
      return { className: 'parag--lg' }
    } else if (data.label === 'inline-link') {
      return { className: 'inline-link' }
    } else if (data.label === 'image-caption') {
      return { className: 'image-caption' }
    } else if (data.label === 'image-left-caption') {
      return { className: 'image-left-caption' }
    } else if (data.label === 'quote') {
      return { className: 'quote' }
    }
  }

  return {}
}

const getElemType = (span: SpanType) => {
  const { type } = span

  if (type === 'strong') {
    return 'strong'
  } else if (type === 'hyperlink') {
    return 'a'
  }

  return 'span'
}

const getRoot = (element: RawType, key: number) => {
  if (element.type === 'paragraph') {
    return createElement('p', { key }, [])
  } else if (element.type === 'list-item' || element.type === 'o-list-item') {
    return createElement('li', { key, className: 'list-item pl-2' }, [])
  }
  return createElement('span', { key }, [])
}

const getElem = (key: number, spans: SpanType[]) => {
  const span = spans.find(span => span.start === key)

  if (span) {
    const elemType: any = getElemType(span)
    const attributes = getAttributes(span)
    span.start = -1
    return createElement(elemType, { key, children: '', ...attributes }, [])
  }
  return createElement('p', { key }, [])
}

const labelDict = (element: RawType, spans: SpanType[] = [], key: number) => {
  const ends = spans.map(s => s.end)
  const text = element.text || ''
  const starts = spans.map(s => s.start)
  let root: any = getRoot(element, key)
  let str = ''
  let elem: any = null
  let prevElem: any = null

  for (let i = 0; i < text.length; i++) {
    if (i === text.length - 1) {
      str += text[i]
    }
    if (spans.map(s => s.start).includes(i)) {
      if (elem) {
        str && elem.props.children.push(str)
        str = ''
        let newElem = getElem(i, spans)
        elem.props.children.push(newElem)
        prevElem = elem
        elem = newElem
      } else {
        str && root.props.children.push(str)
        str = ''
        elem = getElem(i, spans)
        root.props.children.push(elem)
      }
    }

    if (ends.includes(i) || i === text.length - 1) {
      if (elem) {
        str && elem.props.children.push(str)
        str = ''
        if (prevElem) {
          if (spans[starts.indexOf(parseInt(prevElem.key))].end > i) {
            elem = prevElem
          }
        }
        elem = null
      } else {
        const start = spans.find(s => s.end === i || s.end === i + 1)?.start
        if (start) {
          elem = getElem(start, spans)
          str && elem.props.children.push(str)
          root.props.children.push(elem)
          elem = null
          str = ''
        }
      }
    }

    if (spans.map(s => s.start).includes(i)) {
      i--
    } else {
      str += text[i]
    }
  }
  if (text.length > 0 && str) {
    str = str.substring(0, str.length - 1)

    if (elem) {
      elem.props.children.push(str)
    } else {
      root.props.children.push(str)
    }
  }
  if (element.type === 'list-item' || element.type === 'o-list-item') {
    return root
  }
  return createElement('div', { key, className: 'rich-text__paragraph' }, root)
}

const Paragraph = (element: RawType, key: number, linkResolver?: (page: string) => string) => {
  let spans: SpanType[] = JSON.parse(JSON.stringify(element.spans)) // Make a copy of the element spans
  spans = spans?.sort((a, b) => b.type.localeCompare(a.type))
  if (linkResolver) {
    getLink = linkResolver
  }
  return labelDict(element, spans, key)
}

export default Paragraph
